/**
 * デジタルレシート送信コンポーネント
 * メール/SMSでレシートを送信
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const DigitalReceipt = ({
  orderId,
  amount,
  onSuccess,
  onError,
  onClose,
}) => {
  const [method, setMethod] = useState('email');
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  // 送信方法変更
  const handleMethodChange = (event, newMethod) => {
    if (newMethod) {
      setMethod(newMethod);
      setDestination('');
      setError(null);
    }
  };

  // バリデーション
  const validateDestination = () => {
    if (!destination.trim()) {
      setError(method === 'email' ? 'メールアドレスを入力してください' : '電話番号を入力してください');
      return false;
    }

    if (method === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(destination)) {
        setError('有効なメールアドレスを入力してください');
        return false;
      }
    }

    if (method === 'sms') {
      const phoneRegex = /^[0-9]{10,11}$/;
      const cleaned = destination.replace(/[^0-9]/g, '');
      if (!phoneRegex.test(cleaned)) {
        setError('有効な電話番号を入力してください（10-11桁）');
        return false;
      }
    }

    return true;
  };

  // 電話番号フォーマット
  const formatPhoneInput = (value) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 7) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 11)}`;
  };

  // 入力ハンドラ
  const handleDestinationChange = (e) => {
    let value = e.target.value;
    if (method === 'sms') {
      value = formatPhoneInput(value);
    }
    setDestination(value);
    setError(null);
  };

  // 送信
  const handleSend = async () => {
    if (!validateDestination()) return;

    setLoading(true);
    setError(null);

    try {
      const cleanedDestination = method === 'sms'
        ? destination.replace(/[^0-9]/g, '')
        : destination;

      const response = await api.post('/receipts/send', {
        orderId,
        method,
        destination: cleanedDestination,
      });

      if (response.data.success) {
        setSuccess(true);
        setReceiptUrl(response.data.receiptUrl);
        onSuccess?.(response.data);
      } else {
        throw new Error(response.data.message || '送信に失敗しました');
      }
    } catch (err) {
      console.error('レシート送信エラー:', err);
      setError(err.response?.data?.message || err.message || '送信に失敗しました');
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  // URLコピー
  const handleCopyUrl = async () => {
    if (receiptUrl) {
      try {
        await navigator.clipboard.writeText(receiptUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('コピー失敗:', err);
      }
    }
  };

  // 成功画面
  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          送信完了
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {method === 'email' ? 'メール' : 'SMS'}でレシートを送信しました
        </Typography>

        <Chip
          label={destination}
          color="primary"
          variant="outlined"
          sx={{ mb: 3 }}
        />

        {receiptUrl && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              レシートURL
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={receiptUrl}
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleCopyUrl}
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
              >
                {copied ? 'コピー済み' : 'コピー'}
              </Button>
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          onClick={onClose}
          fullWidth
          sx={{ mt: 3 }}
        >
          閉じる
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        デジタルレシート送信
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        レシートをメールまたはSMSで送信できます
      </Typography>

      {/* 金額表示 */}
      {amount && (
        <Box sx={{ textAlign: 'center', mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
          <Typography variant="caption" color="primary.contrastText">
            お会計金額
          </Typography>
          <Typography variant="h4" color="primary.contrastText" fontWeight="bold">
            ¥{amount.toLocaleString()}
          </Typography>
        </Box>
      )}

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 送信方法選択 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          送信方法
        </Typography>
        <ToggleButtonGroup
          value={method}
          exclusive
          onChange={handleMethodChange}
          fullWidth
        >
          <ToggleButton value="email">
            <EmailIcon sx={{ mr: 1 }} />
            メール
          </ToggleButton>
          <ToggleButton value="sms">
            <SmsIcon sx={{ mr: 1 }} />
            SMS
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 送信先入力 */}
      <TextField
        fullWidth
        label={method === 'email' ? 'メールアドレス' : '電話番号'}
        value={destination}
        onChange={handleDestinationChange}
        placeholder={method === 'email' ? 'example@email.com' : '090-1234-5678'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {method === 'email' ? <EmailIcon /> : <SmsIcon />}
            </InputAdornment>
          ),
        }}
        type={method === 'email' ? 'email' : 'tel'}
        inputProps={{
          inputMode: method === 'email' ? 'email' : 'tel',
        }}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 2 }} />

      {/* アクションボタン */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loading || !destination.trim()}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          sx={{ flex: 2 }}
        >
          {loading ? '送信中...' : 'レシートを送信'}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
        ※ 送信されたレシートは90日間有効です
      </Typography>
    </Box>
  );
};

export default DigitalReceipt;
